<template>
  <vue-autonumeric
    v-model="valueProxy"
    v-bind="inputOptions"
    ref="autonumericElement"
    :class="cssClass"
    :options="options"
    @keyup.enter.native="onEnterHandler($event, valueProxy)"
    @blur.native="onBlurHandler"
  />
</template>

<script>

import { VueAutonumeric } from 'vue-autonumeric/src';

const DECIMAL_SEPARATOR = 'decimal-number.decimal-separator';
const THOUSANDS_SEPARATOR = 'decimal-number.thousand-separator';
const PREFIX = '';
const PRECISION = 2;

export default {
  name: 'sNumberInput',
  components: {
    VueAutonumeric,
  },
  props: {
    value: {
      type: [String, Number],
    },
    inputOptions: {
      type: Object,
      default: () => ({}),
    },
    numericOptions: {
      type: [Object, Array],
      default: () => ({}),
    },
    validation: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
  },
  data: vm => ({
    defaultOptions: {
      allowDecimalPadding: true,
      alwaysAllowDecimalCharacter: false,
      caretPositionOnFocus: null,
      createLocalList: true,
      currencySymbol: PREFIX,
      currencySymbolPlacement: 'p',
      decimalCharacter: vm.$t(DECIMAL_SEPARATOR),
      decimalCharacterAlternative: '.',
      decimalPlaces: PRECISION,
      decimalPlacesRawValue: null,
      decimalPlacesShownOnBlur: null,
      decimalPlacesShownOnFocus: null,
      defaultValueOverride: null,
      digitalGroupSpacing: '3',
      digitGroupSeparator: vm.$t(THOUSANDS_SEPARATOR),
      divisorWhenUnfocused: null,
      emptyInputBehavior: 'null',
      eventBubbles: true,
      eventIsCancelable: true,
      failOnUnknownOption: false,
      formatOnPageLoad: true,
      formulaMode: false,
      historySize: 20,
      isCancellable: true,
      leadingZero: 'deny',
      maximumValue: '10000000000000',
      minimumValue: -'10000000000000',
      modifyValueOnWheel: true,
      negativeBracketsTypeOnBlur: null,
      negativePositiveSignPlacement: null,
      negativeSignCharacter: '-',
      noEventListeners: false,
      onInvalidPaste: 'error',
      outputFormat: 'string',
      overrideMinMaxLimits: null,
      positiveSignCharacter: '+',
      rawValueDivisor: null,
      readOnly: false,
      roundingMethod: 'S',
      saveValueToSessionStorage: false,
      selectNumberOnly: true,
      selectOnFocus: true,
      serializeSpaces: '+',
      showOnlyNumbersOnFocus: false,
      showPositiveSign: false,
      showWarnings: true,
      styleRules: null,
      suffixText: '',
      symbolWhenUnfocused: '',
      unformatOnHover: true,
      unformatOnSubmit: false,
      valuesToStrings: null,
      watchExternalChanges: false,
      wheelOn: 'focus',
      wheelStep: 'progressive',
    },
  }),
  computed: {
    valueProxy: {
      get() {
        return this.value === undefined ? null : this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    options() {
      return {
        ...this.defaultOptions,
        ...this.numericOptions,
        ...this.minimumAndMaximumMaskValue,
        readOnly: this.disabled,
      };
    },
    minimumAndMaximumMaskValue() {
      const regex = new RegExp('[0-8]', 'g');

      const { minValue, maxValue, between } = this.validation?.$params || {};

      const betweenValidation = between?.$sub != null
        ? between.$sub[0]
        : between;

      let minimumValue = minValue?.min ?? betweenValidation?.min
        ?? this.numericOptions?.minimumValue
        ?? this.defaultOptions.minimumValue;

      let maximumValue = maxValue?.max ?? betweenValidation?.max
        ?? this.numericOptions?.maximumValue
        ?? this.defaultOptions.maximumValue;

      minimumValue = minimumValue.toString().replace(regex, '9');
      maximumValue = maximumValue.toString().replace(regex, '9');

      return {
        minimumValue: minimumValue > 0 ? '0' : `${minimumValue}`,
        maximumValue,
      };
    },
    cssClass() {
      const haveAnInvalidValidation = (this.validation?.$invalid && this.validation?.$dirty)
        || false;

      return {
        'is-invalid': this.invalid || this.inputOptions?.invalid || haveAnInvalidValidation,
        'form-control': true,
      };
    },
  },
  methods: {
    onEnterHandler() {
      const { anElement } = this.$refs.autonumericElement;
      const { initialRawValueOnFirstKeydown } = anElement;
      if (!initialRawValueOnFirstKeydown || initialRawValueOnFirstKeydown === '') return;
      anElement.set(parseFloat(initialRawValueOnFirstKeydown));
    },
    onBlurHandler() {
      this.$emit('blur-native-proxy');
    },
  },
};

</script>
